import ja from "date-fns/locale/ja";
import en from "date-fns/locale/en";
import ko from "date-fns/locale/ko";
import zhCN from "date-fns/locale/zh_cn";
import zhTW from "date-fns/locale/zh_tw";

export type DateFnsLocale = typeof ja;

// ==========================================
// 定数
// ==========================================
export const LOCALE_CONFIG = {
  STORAGE_KEY: "user-locale-preference",
  DEFAULT_LOCALE: "en",
  SUPPORTED_LOCALES: ["ja", "en", "zh-TW", "zh-CN", "ko"] as const,
} as const;

type SupportedLocale = typeof LOCALE_CONFIG.SUPPORTED_LOCALES[number];

// ブラウザ言語マッピング（小文字で定義）
const LOCALE_MAP: Record<string, string> = {
  ja: "ja",
  "ja-jp": "ja",
  en: "en",
  "en-us": "en",
  "en-gb": "en",
  "zh-tw": "zh-TW",
  "zh-hk": "zh-TW",
  "zh-cn": "zh-CN",
  zh: "zh-CN",
  ko: "ko",
  "ko-kr": "ko",
};

// date-fns ロケールマッピング
const DATE_FNS_LOCALE_MAP: Record<string, DateFnsLocale> = {
  ja,
  en,
  ko,
  "zh-CN": zhCN,
  "zh-TW": zhTW,
};

// 言語表示名
export const LOCALE_LABELS: Record<string, string> = {
  system: "システム",
  ja: "日本語",
  en: "English",
  "zh-TW": "繁體中文",
  "zh-CN": "简体中文",
  ko: "한국어",
};

// ==========================================
// 型定義
// ==========================================
interface LocalePreference {
  mode: "system" | "manual";
  locale?: SupportedLocale;
}

// ==========================================
// メイン関数
// ==========================================

/**
 * ロケールを検出
 * @returns 現在のロケール設定
 */
export function detectUserLocale() {
  const pref = getPreference();

  // 手動選択されている場合
  if (pref?.mode === "manual" && pref.locale) {
    return pref.locale;
  }

  // システム設定または初回
  const browserLang = getBrowserLanguage();
  const locale = normalizeLocale(browserLang);

  // 初回時はシステムモードで保存
  if (!pref) {
    savePreference("system");
  }

  return locale;
}

/**
 * ユーザーが言語を選択
 * @param selection 選択された言語または"system"
 */
export function setUserLocale(selection: string) {
  const previousLocale = detectUserLocale();

  if (selection === "system") {
    savePreference("system");
  } else if (LOCALE_CONFIG.SUPPORTED_LOCALES.includes(selection as SupportedLocale)) {
    savePreference("manual", selection as SupportedLocale);
  } else {
    // 無効な選択の場合はデフォルトにフォールバック
    console.warn(`Unsupported locale: ${selection}, falling back to system`);
    savePreference("system");
  }

  const newLocale = detectUserLocale();
  if (previousLocale !== newLocale) {
    // カスタムイベントを発火
    window.dispatchEvent(
      new CustomEvent("localeChanged", {
        detail: { previous: previousLocale, current: newLocale },
      })
    );
  }
}

/**
 * 現在の設定を取得（UI用）
 * @returns 現在の言語設定または"system"
 */
export function getCurrentMode() {
  const pref = getPreference();
  return pref?.mode === "manual" && pref.locale ? pref.locale : "system";
}

/**
 * ログアウト時（言語設定を保持）
 */
export function clearStorageKeepingLocale() {
  const pref = localStorage.getItem(LOCALE_CONFIG.STORAGE_KEY);
  localStorage.clear();
  if (pref) {
    localStorage.setItem(LOCALE_CONFIG.STORAGE_KEY, pref);
  }
}

/**
 * システムのロケールを取得（内部ヘルパー関数）
 * @returns システムロケール文字列（小文字正規化済み）、取得失敗時はnull
 */
function getSystemLocale(): string | null {
  try {
    const intlLocale = new Intl.DateTimeFormat().resolvedOptions().locale;
    return intlLocale.toLowerCase();
  } catch (error) {
    console.warn("Failed to get Intl locale:", error);
    return null;
  }
}

/**
 * 利用規約・プライバシーポリシー用のロケールを取得
 * @returns microCMS用のロケール文字列（ja-JP または en-US）
 */
export function getLocaleForLegalDocuments() {
  const locale = getSystemLocale();

  // 日本語ロケールのパターンをチェック
  // ja, ja-jp, ja-JP などの形式に対応
  if (locale && locale.startsWith("ja")) {
    return "ja-JP";
  }

  // TODO: 現在、法務を通して正式に作成されている規約は日本語と英語のみ
  // そのため、日本語以外のロケールからのアクセスに対しては英語の規約を表示する
  // 将来的に他言語の規約が法務を通して正式に作成された場合は、
  // 該当言語のマッピングを追加する必要がある

  // 日本語以外はすべて英語として扱う
  return "en-US";
}

/**
 * システムのロケールが日本リージョンか判定
 * @returns 日本リージョンの場合はtrue、それ以外はfalse
 */
export function isJapaneseRegion() {
  const locale = getSystemLocale();
  return locale?.startsWith("ja") ?? false;
}

/**
 * i18nロケール文字列に対応するdate-fnsロケールオブジェクトを取得
 * @param locale i18nロケール文字列（例: "ja", "en", "zh-CN"）
 * @returns date-fnsロケールオブジェクト（未対応の場合はjaにフォールバック）
 */
export function getDateFnsLocale(locale: string): DateFnsLocale {
  return DATE_FNS_LOCALE_MAP[locale] || ja;
}

// ==========================================
// 内部関数
// ==========================================

/**
 * ローカルストレージから設定を取得
 * @returns ロケール設定またはnull
 */
function getPreference(): LocalePreference | null {
  try {
    const data = localStorage.getItem(LOCALE_CONFIG.STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error("Failed to get locale preference:", error);
    return null;
  }
}

/**
 * ローカルストレージに設定を保存
 * @param mode 設定モード
 * @param locale 言語（オプション）
 */
function savePreference(mode: "system" | "manual", locale?: SupportedLocale) {
  try {
    const pref: LocalePreference = { mode, locale };
    localStorage.setItem(LOCALE_CONFIG.STORAGE_KEY, JSON.stringify(pref));
  } catch (error) {
    console.error("Failed to save locale preference:", error);
  }
}

/**
 * ブラウザの言語設定を取得
 * @returns ブラウザの言語設定
 */
function getBrowserLanguage() {
  return (navigator.language || LOCALE_CONFIG.DEFAULT_LOCALE).toLowerCase();
}

/**
 * ブラウザ言語を正規化
 * @param browserLang ブラウザの言語設定
 * @returns 正規化されたロケール
 */
function normalizeLocale(browserLang: string) {
  // 完全一致
  if (LOCALE_MAP[browserLang]) {
    return LOCALE_MAP[browserLang];
  }

  // 言語コードのみ（例: en-US → en）
  const lang = browserLang.split("-")[0];
  if (LOCALE_MAP[lang]) {
    return LOCALE_MAP[lang];
  }

  // デフォルト
  return LOCALE_CONFIG.DEFAULT_LOCALE;
}
